import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCheckCircle, FaCalendarAlt, FaUser, FaEnvelope, FaPhone, FaBed, FaExpand, FaStar } from 'react-icons/fa';
import { useBooking } from '../context/BookingContext';
import { rooms } from '../data/hotelData';
import './Booking.css';

const steps = ['Search & Select', 'Guest Info', 'Confirm'];

const TAX_RATE = 0.12;

export default function Booking() {
  const { bookingData, updateBooking, confirmBooking } = useBooking();
  const [step, setStep] = useState(0);
  const [confirmed, setConfirmed] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [guest, setGuest] = useState({
    firstName: '', lastName: '', email: '',
    phone: '', country: '', specialRequests: '',
  });

  const today = new Date().toISOString().split('T')[0];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];

  // Dynamic nights calculation
  const nights = useMemo(() => {
    if (!bookingData.checkIn || !bookingData.checkOut) return 1;
    const diff = Math.ceil(
      (new Date(bookingData.checkOut) - new Date(bookingData.checkIn)) / 86400000
    );
    return Math.max(1, diff);
  }, [bookingData.checkIn, bookingData.checkOut]);

  // Filter available rooms based on roomType and guest count
  const availableRooms = useMemo(() => {
    return rooms.filter(r => {
      const typeMatch = bookingData.roomType === 'any' || r.category === bookingData.roomType;
      const capacityMatch = r.capacity >= Number(bookingData.guests || 1);
      return typeMatch && capacityMatch;
    });
  }, [bookingData.roomType, bookingData.guests]);

  // Auto-select first available room when filter changes
  const effectiveRoom = selectedRoom && availableRooms.find(r => r.id === selectedRoom.id)
    ? selectedRoom
    : availableRooms[0] || null;

  // Dynamic price calculation
  const roomPrice = effectiveRoom ? effectiveRoom.price : 0;
  const subtotal = roomPrice * nights;
  const taxes = Math.round(subtotal * TAX_RATE);
  const total = subtotal + taxes;

  const handleGuestChange = (e) => {
    const { name, value } = e.target;
    setGuest(prev => ({ ...prev, [name]: value }));
  };

  const handleRoomSelect = (room) => {
    setSelectedRoom(room);
  };

  const handleStep0Continue = () => {
    if (!effectiveRoom) return;
    setStep(1);
  };

  const handleConfirm = () => {
    const booking = confirmBooking({
      ...bookingData,
      guest,
      nights,
      roomName: effectiveRoom?.name,
      roomPrice,
      subtotal,
      taxes,
      totalPrice: total,
    });
    setConfirmed(booking);
  };

  // ── Confirmed screen ────────────────────────────────────────────────────────
  if (confirmed) {
    return (
      <main>
        <div className="page-banner">
          <div className="page-banner-content">
            <h1>Booking Confirmed!</h1>
          </div>
        </div>
        <section className="section">
          <div className="container">
            <motion.div
              className="booking-confirmed"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <FaCheckCircle className="confirmed-icon" />
              <h2>Your Reservation is Confirmed</h2>
              <p className="confirmed-id">Booking ID: <strong>#{confirmed.id}</strong></p>
              <div className="confirmed-details">
                <div className="confirmed-row"><span>Room</span><strong>{confirmed.roomName}</strong></div>
                <div className="confirmed-row"><span>Check-in</span><strong>{bookingData.checkIn}</strong></div>
                <div className="confirmed-row"><span>Check-out</span><strong>{bookingData.checkOut}</strong></div>
                <div className="confirmed-row"><span>Nights</span><strong>{nights}</strong></div>
                <div className="confirmed-row"><span>Guests</span><strong>{bookingData.guests}</strong></div>
                <div className="confirmed-row"><span>Room rate</span><strong>${roomPrice}/night</strong></div>
                <div className="confirmed-row"><span>Taxes (12%)</span><strong>${taxes}</strong></div>
                <div className="confirmed-row" style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                  <span><strong>Total Paid</strong></span>
                  <strong style={{ color: 'var(--primary)', fontSize: '1.2rem' }}>${total}</strong>
                </div>
              </div>
              <p className="confirmed-note">
                A confirmation email has been sent to <strong>{guest.email}</strong>.
                Our concierge will reach out within 24 hours.
              </p>
              <a href="/" className="btn btn-primary">Back to Home</a>
            </motion.div>
          </div>
        </section>
      </main>
    );
  }

  // ── Main booking flow ───────────────────────────────────────────────────────
  return (
    <main>
      <div className="page-banner">
        <div className="page-banner-content">
          <span className="section-badge">Reservations</span>
          <h1>Book Your Stay</h1>
          <p>Reserve your luxury experience</p>
        </div>
      </div>

      <section className="section booking-page">
        <div className="container">

          {/* Step indicator */}
          <div className="booking-steps">
            {steps.map((s, i) => (
              <div key={s} className={`booking-step ${i <= step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
                <div className="step-number">{i < step ? <FaCheckCircle /> : i + 1}</div>
                <span>{s}</span>
              </div>
            ))}
          </div>

          <div className="booking-layout">
            {/* ── Left column: form steps ── */}
            <div className="booking-form-col">
              <AnimatePresence mode="wait">

                {/* STEP 0 — Search & Select Room */}
                {step === 0 && (
                  <motion.div key="step0" className="booking-step-content"
                    initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.4 }}>

                    <h2>Search & Select Room</h2>

                    {/* Dates / guests / type filters */}
                    <div className="form-grid">
                      <div className="form-group">
                        <label><FaCalendarAlt /> Check-In Date</label>
                        <input type="date" value={bookingData.checkIn || today} min={today}
                          onChange={e => updateBooking({ checkIn: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label><FaCalendarAlt /> Check-Out Date</label>
                        <input type="date" value={bookingData.checkOut || tomorrow}
                          min={bookingData.checkIn || today}
                          onChange={e => updateBooking({ checkOut: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label><FaUser /> Guests</label>
                        <select value={bookingData.guests}
                          onChange={e => updateBooking({ guests: Number(e.target.value) })}>
                          {[1, 2, 3, 4, 5, 6].map(n =>
                            <option key={n} value={n}>{n} Guest{n > 1 ? 's' : ''}</option>
                          )}
                        </select>
                      </div>
                      <div className="form-group">
                        <label><FaBed /> Room Type</label>
                        <select value={bookingData.roomType}
                          onChange={e => { updateBooking({ roomType: e.target.value }); setSelectedRoom(null); }}>
                          <option value="any">Any Room</option>
                          <option value="classic">Classic</option>
                          <option value="deluxe">Deluxe</option>
                          <option value="superior">Superior</option>
                          <option value="suite">Suite</option>
                        </select>
                      </div>
                    </div>

                    {/* Available rooms to pick */}
                    <div className="available-rooms-section">
                      <div className="available-rooms-header">
                        <h3>Available Rooms</h3>
                        <span className="rooms-found-badge">
                          {availableRooms.length} room{availableRooms.length !== 1 ? 's' : ''} found
                        </span>
                      </div>

                      {availableRooms.length === 0 ? (
                        <div className="no-rooms-msg">
                          <p>No rooms available for {bookingData.guests} guests in the <strong>{bookingData.roomType}</strong> category.</p>
                          <button className="btn btn-outline" onClick={() => updateBooking({ roomType: 'any', guests: 2 })}>
                            Reset Filters
                          </button>
                        </div>
                      ) : (
                        <div className="room-pick-list">
                          {availableRooms.map(room => {
                            const isSelected = effectiveRoom?.id === room.id;
                            return (
                              <motion.div
                                key={room.id}
                                className={`room-pick-card ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleRoomSelect(room)}
                                whileHover={{ scale: 1.01 }}
                                transition={{ duration: 0.2 }}
                              >
                                <div className="room-pick-img">
                                  <img src={room.image} alt={room.name} />
                                  {isSelected && (
                                    <div className="room-pick-selected-badge">
                                      <FaCheckCircle /> Selected
                                    </div>
                                  )}
                                </div>
                                <div className="room-pick-info">
                                  <div className="room-pick-top">
                                    <div>
                                      <span className="room-pick-category">{room.category}</span>
                                      <h4>{room.name}</h4>
                                      <div className="room-pick-rating">
                                        <FaStar /> {room.rating} <span>({room.reviews} reviews)</span>
                                      </div>
                                    </div>
                                    <div className="room-pick-price">
                                      <strong>${room.price}</strong>
                                      <span>/night</span>
                                      <div className="room-pick-total-hint">
                                        ${room.price * nights} total
                                      </div>
                                    </div>
                                  </div>
                                  <div className="room-pick-meta">
                                    <span><FaExpand /> {room.size} m²</span>
                                    <span><FaUser /> Up to {room.capacity}</span>
                                    <span><FaBed /> {room.beds}</span>
                                    <span>👁 {room.view}</span>
                                  </div>
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <button
                      className="btn btn-primary step-btn"
                      onClick={handleStep0Continue}
                      disabled={!effectiveRoom}
                    >
                      Continue to Guest Info
                    </button>
                  </motion.div>
                )}

                {/* STEP 1 — Guest Info */}
                {step === 1 && (
                  <motion.div key="step1" className="booking-step-content"
                    initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.4 }}>

                    <h2>Guest Information</h2>
                    <form onSubmit={e => { e.preventDefault(); setStep(2); }}>
                      <div className="form-grid">
                        <div className="form-group">
                          <label><FaUser /> First Name</label>
                          <input type="text" name="firstName" value={guest.firstName}
                            onChange={handleGuestChange} placeholder="John" required />
                        </div>
                        <div className="form-group">
                          <label><FaUser /> Last Name</label>
                          <input type="text" name="lastName" value={guest.lastName}
                            onChange={handleGuestChange} placeholder="Smith" required />
                        </div>
                        <div className="form-group">
                          <label><FaEnvelope /> Email Address</label>
                          <input type="email" name="email" value={guest.email}
                            onChange={handleGuestChange} placeholder="john@example.com" required />
                        </div>
                        <div className="form-group">
                          <label><FaPhone /> Phone Number</label>
                          <input type="tel" name="phone" value={guest.phone}
                            onChange={handleGuestChange} placeholder="+1 234 567 8900" required />
                        </div>
                        <div className="form-group form-group--full">
                          <label>Country of Residence</label>
                          <input type="text" name="country" value={guest.country}
                            onChange={handleGuestChange} placeholder="United States" />
                        </div>
                        <div className="form-group form-group--full">
                          <label>Special Requests (optional)</label>
                          <textarea name="specialRequests" value={guest.specialRequests}
                            onChange={handleGuestChange} rows={3}
                            placeholder="Early check-in, anniversary setup, dietary requirements..." />
                        </div>
                      </div>
                      <div className="step-actions">
                        <button type="button" className="btn btn-outline" onClick={() => setStep(0)}>Back</button>
                        <button type="submit" className="btn btn-primary">Review Booking</button>
                      </div>
                    </form>
                  </motion.div>
                )}

                {/* STEP 2 — Confirm */}
                {step === 2 && (
                  <motion.div key="step2" className="booking-step-content"
                    initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.4 }}>

                    <h2>Confirm Your Booking</h2>

                    <div className="confirm-section">
                      <h3>Stay Details</h3>
                      <div className="confirm-rows">
                        <div className="confirm-row"><span>Room</span><strong>{effectiveRoom?.name}</strong></div>
                        <div className="confirm-row"><span>Category</span><strong style={{ textTransform: 'capitalize' }}>{effectiveRoom?.category}</strong></div>
                        <div className="confirm-row"><span>Check-in</span><strong>{bookingData.checkIn}</strong></div>
                        <div className="confirm-row"><span>Check-out</span><strong>{bookingData.checkOut}</strong></div>
                        <div className="confirm-row"><span>Nights</span><strong>{nights}</strong></div>
                        <div className="confirm-row"><span>Guests</span><strong>{bookingData.guests}</strong></div>
                      </div>
                    </div>

                    <div className="confirm-section">
                      <h3>Guest Details</h3>
                      <div className="confirm-rows">
                        <div className="confirm-row"><span>Name</span><strong>{guest.firstName} {guest.lastName}</strong></div>
                        <div className="confirm-row"><span>Email</span><strong>{guest.email}</strong></div>
                        <div className="confirm-row"><span>Phone</span><strong>{guest.phone}</strong></div>
                        {guest.country && <div className="confirm-row"><span>Country</span><strong>{guest.country}</strong></div>}
                      </div>
                    </div>

                    <div className="confirm-section">
                      <h3>Payment Summary</h3>
                      <div className="confirm-rows">
                        <div className="confirm-row">
                          <span>Room rate</span>
                          <strong>${roomPrice}/night × {nights} night{nights > 1 ? 's' : ''}</strong>
                        </div>
                        <div className="confirm-row"><span>Subtotal</span><strong>${subtotal}</strong></div>
                        <div className="confirm-row"><span>Taxes & fees (12%)</span><strong>${taxes}</strong></div>
                        <div className="confirm-row confirm-total">
                          <span>Total</span><strong>${total}</strong>
                        </div>
                      </div>
                    </div>

                    <p className="booking-terms">
                      By confirming, you agree to our <a href="/terms">Terms & Conditions</a> and
                      <a href="/privacy"> Privacy Policy</a>. Free cancellation up to 48 hours before arrival.
                    </p>

                    <div className="step-actions">
                      <button className="btn btn-outline" onClick={() => setStep(1)}>Back</button>
                      <button className="btn btn-primary" onClick={handleConfirm}>Confirm Reservation</button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* ── Right column: dynamic summary ── */}
            <div className="booking-summary-col">
              <div className="booking-summary-card">
                <h3>Booking Summary</h3>

                {effectiveRoom ? (
                  <>
                    <motion.img
                      key={effectiveRoom.id}
                      src={effectiveRoom.image}
                      alt={effectiveRoom.name}
                      className="summary-room-img"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.4 }}
                    />
                    <motion.div
                      key={`info-${effectiveRoom.id}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="summary-room-header">
                        <h4 className="summary-room-name">{effectiveRoom.name}</h4>
                        <span className="summary-room-cat">{effectiveRoom.category}</span>
                      </div>

                      <div className="summary-rows">
                        <div className="summary-row"><span>Check-in</span><strong>{bookingData.checkIn || '--'}</strong></div>
                        <div className="summary-row"><span>Check-out</span><strong>{bookingData.checkOut || '--'}</strong></div>
                        <div className="summary-row"><span>Duration</span><strong>{nights} night{nights > 1 ? 's' : ''}</strong></div>
                        <div className="summary-row"><span>Guests</span><strong>{bookingData.guests}</strong></div>
                        <div className="summary-row"><span>${roomPrice} × {nights} nights</span><strong>${subtotal}</strong></div>
                        <div className="summary-row"><span>Taxes (12%)</span><strong>${taxes}</strong></div>
                        <div className="summary-row summary-row--total">
                          <span>Total</span>
                          <motion.strong
                            key={total}
                            initial={{ scale: 1.15, color: '#a8844d' }}
                            animate={{ scale: 1, color: '#c9a96e' }}
                            transition={{ duration: 0.3 }}
                          >
                            ${total}
                          </motion.strong>
                        </div>
                      </div>
                    </motion.div>
                  </>
                ) : (
                  <div className="summary-empty">
                    <FaBed />
                    <p>Select a room to see your booking summary</p>
                  </div>
                )}

                <div className="summary-guarantee">
                  <FaCheckCircle /> Best Rate Guaranteed
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
